import {Language} from '@/ts/types/component/localization.type';

/**
 * Countries that read a date month first. Everywhere else is day first, which is what the design
 * spells as `DD.MM.YYYY` (IPD-3229).
 *
 * The list is the gate rather than the language, because the two do not travel together: an
 * English-speaking reader in Germany still reads 25.04.2024, and a German speaker in the States
 * still reads 04/25/2024. `useUnitConversion` already splits imperial from metric the same way.
 */
const MONTH_FIRST_COUNTRIES = ['US'];

/**
 * Countries whose calendar week starts on Sunday. Everywhere else it starts on Monday, the ISO week
 * the app drew for everyone until IPD-3543. Kept apart from the date order: the two coincide for
 * the countries Balm serves today, but they are not the same question.
 */
const SUNDAY_FIRST_COUNTRIES = ['US'];

interface DateFormatOptions {
  /** ISO 3166-1 alpha-2: the account's country, fixed by the deployment at sign-up (IPD-3379). */
  countryCode?: string | null;
  language: Language;
}

interface DateFormatComposable {
  isMonthFirst: boolean;
  /** The day a calendar week starts on, as dayjs numbers it: 0 for Sunday, 1 for Monday. */
  weekStartsOn: 0 | 1;
  /**
   * BCP 47 tag for the places that format through `Intl` rather than dayjs, so those keep the month
   * names they already print (German included) and only the order moves.
   *
   * The region carries the order and is picked from `isMonthFirst`, not from the profile: handing
   * ICU the reader's own region would ask it the same question this file already answered, and the
   * two answers disagree — ICU reads `CA`, `PH` and `PR` as month-first where the list here does
   * not, and a missing country leaves the bare `en`, which is month-first while `numericDate` is
   * day-first. One decision, one place.
   *
   * German is the exception ICU cannot serve: it has no month-first form and drops the region from
   * `de-US`, so a German-reading account in the States keeps German word order here while every
   * digits-only date it sees follows its country.
   */
  intlLocale: string;
  /**
   * BCP 47 tag for numbers, prices and percentages: the reader's language with their own country,
   * since that is what decides the separators ("1,234.5" in the States and Australia, "1.234,5" in
   * Germany). Unlike `intlLocale` it keeps the country as it is, because nothing here has to agree
   * with a second answer. With no country it is `intlLocale`. German is the exception here too: ICU
   * drops the region from `de-US`, so a German-reading account in the States gets German separators.
   */
  numberLocale: string;
  /** The ticket's two forms, digits only: for a date that is read off rather than read aloud. */
  numericDate: string;
  /** Full date as the design draws it: a written-out month wherever the language has one. */
  date: string;
  dateAndTime: string;
  /** Near-future date with no year, for the Today page's unlock lines. */
  dayAndMonth: string;
  time: string;
}

/**
 * The date patterns a reader expects, given where they are and what they read. All dayjs tokens, so
 * a caller can hand them straight to `format()`.
 *
 * Two axes, not one: the country orders the parts, the language names them. Squashing them into a
 * single "locale" is what put 25.04.2024 in front of every reader regardless of profile, and the
 * American order in front of every English one regardless of where they live.
 *
 * German keeps its digits where English gets a month name — not a design choice but a standing
 * limitation: no dayjs locale is loaded anywhere in the app, so `MMM` would print an English
 * "Apr" on the German site.
 */
export function useDateFormat({countryCode, language}: DateFormatOptions): DateFormatComposable {
  const region = /^[a-z]{2}$/iu.test(countryCode ?? '') ? countryCode!.toUpperCase() : '';
  const isMonthFirst = MONTH_FIRST_COUNTRIES.includes(region);
  const isGerman = language === Language.German;
  // Both halves of the tag are guarded, for the same reason: `Intl` throws a RangeError on a
  // malformed one, from inside a render computed, which takes the page down rather than misformats
  // a date. `useLang` casts whatever i18n holds straight to `Language` with nothing in between.
  const tag = Object.values(Language).includes(language) ? language : Language.English;
  // 24-hour with the German unit, 12-hour with a day period in English. Unlike the date, this
  // follows the language: it is read aloud, not read off a form.
  const time = isGerman ? 'HH:mm [Uhr]' : 'h:mm A';
  const numericDate = isMonthFirst ? 'MM/DD/YYYY' : 'DD.MM.YYYY';

  function monthFirst<T>(monthFirstValue: T, dayFirstValue: T): T {
    return isMonthFirst ? monthFirstValue : dayFirstValue;
  }

  const date = isGerman ? numericDate : monthFirst('MMM D, YYYY', 'D MMM YYYY');

  return {
    isMonthFirst,
    weekStartsOn: SUNDAY_FIRST_COUNTRIES.includes(region) ? 0 : 1,
    intlLocale: `${tag}-${isMonthFirst ? 'US' : 'DE'}`,
    numberLocale: `${tag}-${region || (isMonthFirst ? 'US' : 'DE')}`,
    numericDate,
    date,
    // A comma everywhere except where the date already carries one: "Apr 25, 2024, 9:54 AM" reads
    // as a list, while "10 Aug 2026 9:00 AM" and "25.04.2024 09:54 Uhr" run together without it.
    dateAndTime: date.includes(',') ? `${date} ${time}` : `${date}, ${time}`,
    // The trailing dot belongs to the German short date ("25.04." is an abbreviated date); the
    // American numeric form has no such marker, and the English form keeps the weekday the design
    // put there.
    dayAndMonth: isGerman
      ? monthFirst('MM/DD', 'DD.MM.')
      : monthFirst('ddd, MMM D', 'ddd, D MMM'),
    time,
  };
}

export type {DateFormatComposable};
